import * as React from 'react'

import { I18NText } from '@/components/I18NText'
import { cn } from '@/lib'
import { formatSupportedFileTypes } from '@/lib/formatSupportedFileTypes'
import { Ban, Check, FileUp, Upload } from 'lucide-react'
import { LoaderCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button, buttonVariants } from '../Button'
import { Label } from './Label'

export interface FileUploadProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
	supportedFileTypes: Record<string, string>
	onChange: (file: File) => void
	label?: React.ReactNode
	loading?: boolean
	success?: boolean
	error?: React.ReactNode
	reset?: () => void
}

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
	(
		{
			supportedFileTypes,
			onChange,
			label,
			loading,
			success,
			error,
			reset,
			...props
		},
		ref,
	) => {
		const supportedAcceptType = Object.keys(supportedFileTypes)
		const [hoveringFileType, setHoveringFileType] = useState<string | null>(
			null,
		)

		const isValidFileType = useMemo(() => {
			if (hoveringFileType === '') return true
			return hoveringFileType
				? supportedAcceptType.includes(hoveringFileType)
				: false
		}, [supportedAcceptType, hoveringFileType])
		const supportedFormatsString = useMemo(() => {
			return formatSupportedFileTypes(supportedFileTypes)
		}, [supportedFileTypes])

		function setHover(e: React.DragEvent<HTMLDivElement>, hovering: boolean) {
			e.preventDefault()
			setHoveringFileType(hovering ? e.dataTransfer?.items[0]?.type : null)
		}

		function fileReceived(
			e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>,
		) {
			e.preventDefault()
			setHoveringFileType(null)

			const file =
				e.type === 'change'
					? (e as React.ChangeEvent<HTMLInputElement>).target.files?.[0]
					: (e as React.DragEvent<HTMLDivElement>).dataTransfer.files?.[0]
			if (!file) return

			onChange(file)
		}

		return (
			<div
				className={cn(
					'bg-orange-50 border-1 border-dashed border-gray-200 gap-3 flex flex-col h-50 items-center justify-center rounded-lg w-full',
					!!error && 'border-red-500',
					hoveringFileType && 'border-2',
					hoveringFileType && !isValidFileType && 'border border-red-500',
				)}
				onDrop={fileReceived}
				onDragOver={(e) => setHover(e, true)}
				onDragLeave={(e) => setHover(e, false)}
			>
				{loading ? (
					<LoaderCircle className="text-purple-700 h-10 w-10 animate-spin" />
				) : success ? (
					<p className="text-green-500 text-sm">
						<Check className="h-10 w-10" />
					</p>
				) : hoveringFileType ? (
					isValidFileType ? (
						<Upload className="text-purple-700 pointer-events-none" size={36} />
					) : (
						<>
							<Ban
								className="text-(--destructive) pointer-events-none"
								size={36}
							/>
							<p className="text-red-500 pointer-events-none">
								<I18NText
									text="forms.supportedFormats"
									values={{
										supportedFormats: supportedFormatsString,
									}}
								/>
							</p>
						</>
					)
				) : error ? (
					<>
						<Button onClick={reset} className="mb-2">
							<I18NText text="forms.retry" />
						</Button>
						<p className="m-2 text-red-500 text-center text-sm justify-center">
							{error}
						</p>
					</>
				) : (
					<>
						<FileUp className="text-purple-700" size={36} />
						<p className="text-purple-700">
							<I18NText
								text="forms.supportedFormats"
								values={{
									supportedFormats: supportedFormatsString,
								}}
							/>
						</p>
						<Label
							htmlFor="file-upload"
							className={cn(buttonVariants({ variant: 'primary' }))}
						>
							{label}
						</Label>
					</>
				)}
				<input
					id="file-upload"
					className="hidden"
					type="file"
					accept={supportedAcceptType.join(', ')}
					ref={ref}
					onChange={fileReceived}
					{...props}
				/>
			</div>
		)
	},
)
FileUpload.displayName = 'FileUpload'

export { FileUpload }
